import { Head, usePage } from "@inertiajs/react"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  FiscalDataForm,
  type CatalogOption,
  type TaxProfile,
} from "@/features/staff/tax-profile/fiscal-data-form"
import { t } from "@/lib/i18n"

/**
 * The restaurant's CFDI issuer identity.
 *
 * Its own screen rather than a card on the location page, because this is where
 * the PAC credential is entered and a credential field parked between a logo
 * upload and a tip percentage invites exactly the casualness it must not get.
 * The screen exists whether or not a row does — entering the row is what it is
 * for — so unlike the tax-rates card there is no "render nothing" state here.
 * The one thing that hides it entirely is the feature being off, and that is
 * enforced by `Staff::Location::FeatureGuard` on the controller, not by this
 * page.
 */

type Props = {
  taxProfile: TaxProfile
  taxRegimeOptions: CatalogOption[]
  pacEnvironmentOptions: CatalogOption[]
  errors?: Record<string, string | string[]>
}

const tShow = (key: string) => t(`ui.react.pages.staff.location.manage.tax_profile.show.${key}`)

export default function StaffLocationManageTaxProfileShow() {
  const { taxProfile, taxRegimeOptions, pacEnvironmentOptions, errors = {} } = usePage<Props>().props

  // Two separate facts, and the badge reports both together because a manager
  // only cares about the conjunction: identity entered AND a key to stamp with.
  // Either one missing means no invoice can be issued.
  const ready = taxProfile.configured && taxProfile.pacConfigured

  return (
    <StaffLayout>
      <Head title={tShow("title")} />

      <MainContainer size="md">
        <div className="space-y-6">
          <PageHeader
            title={tShow("title")}
            description={tShow("description")}
            actions={
              <StatusBadge
                tone={ready ? "success" : "warning"}
                label={ready ? tShow("badge_ready") : tShow("badge_incomplete")}
              />
            }
          />

          {/* The empty state is a sentence, not a blank form with no
              explanation. A restaurant whose admin has just granted CFDI lands
              here with nothing filled in and no idea that nothing works until
              it is. */}
          {!taxProfile.configured && (
            <p
              className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm"
              data-testid="tax-profile-empty-notice"
            >
              {tShow("empty_notice")}
            </p>
          )}

          <FiscalDataForm
            taxProfile={taxProfile}
            taxRegimeOptions={taxRegimeOptions}
            pacEnvironmentOptions={pacEnvironmentOptions}
            errors={errors}
          />
        </div>
      </MainContainer>
    </StaffLayout>
  )
}
